import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Plus, Filter, Download, Eye, Edit, Search, GraduationCap, FileText, Users, Award, X, Save, CheckCircle2, Building2 } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data for courses
const allCoursesData = Array.from({
  length: 50
}, (_, i) => {
  const statuses = ['In corso', 'Programmato', 'Completato'];
  const status = statuses[i % 3];
  return {
    id: i + 1,
    code: `CRS-${String(i + 1).padStart(3, '0')}`,
    title: i % 4 === 0 ? 'Corso RSPP' : i % 4 === 1 ? 'HACCP Responsabile' : i % 4 === 2 ? 'Antincendio Base' : 'Primo Soccorso',
    type: i % 2 === 0 ? 'Sicurezza' : 'HACCP',
    mode: i % 3 === 0 ? 'Aula' : 'E-learning',
    date: status === 'Completato' ? '2024-03-15' : status === 'In corso' ? '2024-05-20' : '2024-07-10',
    enrolled: 10 + i,
    status,
    instructor: i % 2 === 0 ? 'Dott. Rossi' : 'Dott.ssa Bianchi',
    location: i % 3 === 0 ? 'Sede Arezzo' : 'Online',
    participants: Array.from({
      length: 5 + i % 5
    }, (_, j) => ({
      id: j + 1,
      name: `Partecipante ${j + 1}`,
      company: `Azienda ${j % 3 + 1} SRL`,
      email: `partecipante${j + 1}@email.it`,
      hasCertificate: j % 2 === 0
    }))
  };
});
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'title',
  label: 'Titolo'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'mode',
  label: 'Modalità'
}, {
  key: 'date',
  label: 'Data'
}, {
  key: 'enrolled',
  label: 'Iscritti'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'actions',
  label: 'Azioni'
}];
type ViewType = 'active' | 'scheduled' | 'archive';
export function TrainingPage() {
  const [activeView, setActiveView] = useState<ViewType>('active');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isPracticeModalOpen, setIsPracticeModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  // Filter courses based on active view
  const getFilteredCourses = () => {
    switch (activeView) {
      case 'active':
        return allCoursesData.filter(c => c.status === 'In corso');
      case 'scheduled':
        return allCoursesData.filter(c => c.status === 'Programmato');
      case 'archive':
        return allCoursesData.filter(c => c.status === 'Completato');
      default:
        return allCoursesData;
    }
  };
  const coursesData = getFilteredCourses();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(coursesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['code', 'title']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, activeView]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreateCourse = () => {
    setSelectedItem(null);
    const nextCode = `CRS-${String(allCoursesData.length + 1).padStart(3, '0')}`;
    setFormData({
      code: nextCode,
      status: 'Programmato'
    });
    setIsModalOpen(true);
  };
  const handleCreatePractice = () => {
    setFormData({
      status: 'In valutazione'
    });
    setIsPracticeModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Corso salvato', 'Il corso è stato salvato con successo.');
    }, 1500);
  };
  const handleSavePractice = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsPracticeModalOpen(false);
      addNotification('success', 'Pratica iscrizione creata', 'La pratica è stata creata con stato "In valutazione".');
    }, 1500);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  const viewTabs = [{
    id: 'active' as ViewType,
    label: 'Corsi attivi',
    count: allCoursesData.filter(c => c.status === 'In corso').length
  }, {
    id: 'scheduled' as ViewType,
    label: 'In programma',
    count: allCoursesData.filter(c => c.status === 'Programmato').length
  }, {
    id: 'archive' as ViewType,
    label: 'Archivio corsi',
    count: allCoursesData.filter(c => c.status === 'Completato').length
  }];
  return <Layout data-id="element-2849">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2850">
        <div data-id="element-2851">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2852">
            Modulo formazione
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2853">
            Gestione completa corsi, iscrizioni e certificazioni.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2854">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2856" />} data-id="element-2855">
            Esporta
          </Button>
          <Button variant="flat" color="secondary" onClick={handleCreatePractice} startContent={<FileText className="h-4 w-4" data-id="element-2858" />} data-id="element-2857">
            Pratica iscrizione
          </Button>
          <Button onClick={handleCreateCourse} startContent={<Plus className="h-4 w-4" data-id="element-2860" />} data-id="element-2859">
            Nuovo corso
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="bg-slate-100 p-1 rounded-xl flex items-center w-fit mb-6" data-id="element-2861">
        {viewTabs.map(tab => <button key={tab.id} onClick={() => setActiveView(tab.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${activeView === tab.id ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2862">
            {tab.label}
            <Badge variant={activeView === tab.id ? 'primary' : 'neutral'} size="sm" data-id="element-2863">
              {tab.count}
            </Badge>
          </button>)}
      </div>

      <Card className="mb-6" noPadding data-id="element-2864">
        <div className="p-4 flex flex-col gap-4" data-id="element-2865">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-2866">
            <div className="flex-1" data-id="element-2867">
              <Input placeholder="Cerca corso..." startContent={<Search className="h-4 w-4" data-id="element-2869" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2868" />
            </div>
            <div className="flex gap-2" data-id="element-2870">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2872" />} data-id="element-2871">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2873">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="training" data-id="element-2874" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2875">
              <Select label="Modalità" value={filters.mode || ''} onChange={e => setFilter('mode', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Aula',
            label: 'Aula'
          }, {
            value: 'E-learning',
            label: 'E-learning'
          }]} data-id="element-2876" />
              <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Sicurezza',
            label: 'Sicurezza'
          }, {
            value: 'HACCP',
            label: 'HACCP'
          }]} data-id="element-2877" />
              <div className="col-span-full flex justify-end" data-id="element-2878">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2880" />} data-id="element-2879">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-2881">
        <div className="overflow-x-auto" data-id="element-2882">
          <table className="w-full text-left border-collapse table-fixed" data-id="element-2883">
            <thead data-id="element-2884">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2885">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-right w-32' : ''}`} data-id="element-2886">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2887">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2888">
                  {visibleColumns.includes('code') && <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-2889">
                      {item.code}
                    </td>}
                  {visibleColumns.includes('title') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2890">
                      {item.title}
                    </td>}
                  {visibleColumns.includes('type') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2891">
                      {item.type}
                    </td>}
                  {visibleColumns.includes('mode') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2892">
                      {item.mode}
                    </td>}
                  {visibleColumns.includes('date') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2893">
                      {item.date}
                    </td>}
                  {visibleColumns.includes('enrolled') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2894">
                      {item.enrolled}
                    </td>}
                  {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-2895">
                      <Badge variant={item.status === 'In corso' ? 'success' : item.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-2896">
                        {item.status}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-6 py-4 text-right" data-id="element-2897">
                      <div className="flex items-center justify-end gap-2" data-id="element-2898">
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} title="Visualizza" data-id="element-2899">
                          <Eye className="h-4 w-4 text-slate-500" data-id="element-2900" />
                        </Button>
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} title="Modifica" data-id="element-2901">
                          <Edit className="h-4 w-4 text-seform-orange" data-id="element-2902" />
                        </Button>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-2903">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2904" />
        </div>
      </Card>

      {/* View Course Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio corso" size="2xl" data-id="element-2905">
        {selectedItem && <div className="space-y-6" data-id="element-2906">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-2907">
              <div data-id="element-2908">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2909">
                  Codice
                </span>
                <p className="font-mono font-medium" data-id="element-2910">{selectedItem.code}</p>
              </div>
              <div data-id="element-2911">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2912">
                  Stato
                </span>
                <div className="mt-1" data-id="element-2913">
                  <Badge variant={selectedItem.status === 'In corso' ? 'success' : selectedItem.status === 'Programmato' ? 'info' : 'neutral'} data-id="element-2914">
                    {selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div data-id="element-2915">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2916">
                  Modalità
                </span>
                <p className="font-medium" data-id="element-2917">{selectedItem.mode}</p>
              </div>
              <div className="col-span-full" data-id="element-2918">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2919">
                  Titolo
                </span>
                <p className="font-medium text-lg" data-id="element-2920">{selectedItem.title}</p>
              </div>
              <div data-id="element-2921">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2922">
                  Tipologia
                </span>
                <p className="font-medium" data-id="element-2923">{selectedItem.type}</p>
              </div>
              <div data-id="element-2924">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2925">
                  Data
                </span>
                <p className="font-medium" data-id="element-2926">{selectedItem.date}</p>
              </div>
              <div data-id="element-2927">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2928">
                  Docente
                </span>
                <p className="font-medium" data-id="element-2929">{selectedItem.instructor}</p>
              </div>
              <div data-id="element-2930">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2931">
                  Sede
                </span>
                <p className="font-medium" data-id="element-2932">{selectedItem.location}</p>
              </div>
            </div>

            {/* Participants List */}
            <div data-id="element-2933">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2934">
                <Users className="h-4 w-4" data-id="element-2935" /> Elenco iscritti (
                {selectedItem.participants?.length || 0})
              </h4>
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-2936">
                <table className="w-full text-left" data-id="element-2937">
                  <thead data-id="element-2938">
                    <tr className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold" data-id="element-2939">
                      <th className="px-4 py-3" data-id="element-2940">Nome</th>
                      <th className="px-4 py-3" data-id="element-2941">Azienda</th>
                      <th className="px-4 py-3" data-id="element-2942">Email</th>
                      <th className="px-4 py-3 text-center" data-id="element-2943">Attestato</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-2944">
                    {selectedItem.participants?.map((p: any) => <tr key={p.id} className="hover:bg-slate-50/50" data-id="element-2945">
                        <td className="px-4 py-3 text-sm font-medium text-slate-900" data-id="element-2946">
                          {p.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 flex items-center gap-1" data-id="element-2947">
                          <Building2 className="h-3 w-3 text-slate-400" data-id="element-2948" />
                          {p.company}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600" data-id="element-2949">
                          {p.email}
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-2950">
                          {p.hasCertificate ? <Button variant="light" size="sm" className="!px-2" title="Visualizza attestato" data-id="element-2951">
                              <Award className="h-4 w-4 text-green-600" data-id="element-2952" />
                            </Button> : <span className="text-xs text-slate-400" data-id="element-2953">-</span>}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2954">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-2955">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-2957" />} data-id="element-2956">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Edit Course Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica corso' : 'Nuovo corso'} size="2xl" data-id="element-2958">
        <AnimatePresence mode="wait" data-id="element-2959">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2960">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2961">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2962" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2963">
                Corso salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2964">
              <div className="space-y-6" data-id="element-2965">
                <section data-id="element-2966">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2967">
                    <GraduationCap className="h-4 w-4" data-id="element-2968" /> Dettagli corso
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2969">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} disabled={!!selectedItem} data-id="element-2970" />
                    <Select label="Stato" value={formData.status || 'Programmato'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'Programmato',
                  label: 'Programmato'
                }, {
                  value: 'In corso',
                  label: 'In corso'
                }, {
                  value: 'Completato',
                  label: 'Completato'
                }]} required data-id="element-2971" />
                    <Input label="Titolo" value={formData.title || ''} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} placeholder="" required className="col-span-full" data-id="element-2972" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Sicurezza',
                  label: 'Sicurezza'
                }, {
                  value: 'HACCP',
                  label: 'HACCP'
                }]} required data-id="element-2973" />
                    <Select label="Modalità" value={formData.mode || ''} onChange={e => setFormData({
                  ...formData,
                  mode: e.target.value
                })} options={[{
                  value: 'Aula',
                  label: 'Aula'
                }, {
                  value: 'E-learning',
                  label: 'E-learning'
                }]} required data-id="element-2974" />
                    <DatePicker label="Data" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2975" />
                    <Input label="Docente" value={formData.instructor || ''} onChange={e => setFormData({
                  ...formData,
                  instructor: e.target.value
                })} placeholder="" data-id="element-2976" />
                    <Input label="Sede" value={formData.location || ''} onChange={e => setFormData({
                  ...formData,
                  location: e.target.value
                })} placeholder="" data-id="element-2977" />
                    <Input label="Durata (ore)" type="number" value={formData.duration || ''} onChange={e => setFormData({
                  ...formData,
                  duration: e.target.value
                })} data-id="element-2978" />
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2979">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-2980">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2982" />} data-id="element-2981">
                    Salva corso
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Practice Enrollment Modal */}
      <Modal isOpen={isPracticeModalOpen} onClose={() => setIsPracticeModalOpen(false)} title="Pratica iscrizione" size="lg" data-id="element-2983">
        <AnimatePresence mode="wait" data-id="element-2984">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2985">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2986">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2987" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2988">
                Pratica iscrizione creata
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-2989">
                Stato: In valutazione
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2990">
              <div className="space-y-6" data-id="element-2991">
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex items-start gap-3" data-id="element-2992">
                  <FileText className="h-5 w-5 text-blue-600 mt-0.5" data-id="element-2993" />
                  <div data-id="element-2994">
                    <p className="text-sm text-blue-800 font-medium" data-id="element-2995">
                      Nuova pratica iscrizione
                    </p>
                    <p className="text-xs text-blue-600 mt-1" data-id="element-2996">
                      La pratica verrà creata con stato "In valutazione".
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2997">
                  <Select label="Corso" value={formData.courseId || ''} onChange={e => setFormData({
                ...formData,
                courseId: e.target.value
              })} options={allCoursesData.filter(c => c.status !== 'Completato').map(c => ({
                value: String(c.id),
                label: `${c.code} - ${c.title}`
              }))} required data-id="element-2998" />
                  <Select label="Azienda" value={formData.company || ''} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} options={[{
                value: 'Azienda 1 SRL',
                label: 'Azienda 1 SRL'
              }, {
                value: 'Azienda 2 SRL',
                label: 'Azienda 2 SRL'
              }, {
                value: 'Azienda 3 SRL',
                label: 'Azienda 3 SRL'
              }]} required data-id="element-2999" />
                  <Input label="Nome partecipante" value={formData.participantName || ''} onChange={e => setFormData({
                ...formData,
                participantName: e.target.value
              })} placeholder="" required data-id="element-3000" />
                  <Input label="Email partecipante" type="email" value={formData.participantEmail || ''} onChange={e => setFormData({
                ...formData,
                participantEmail: e.target.value
              })} placeholder="" required data-id="element-3001" />
                  <div className="col-span-full" data-id="element-3002">
                    <Textarea label="Note" value={formData.notes || ''} onChange={e => setFormData({
                  ...formData,
                  notes: e.target.value
                })} placeholder="" data-id="element-3003" />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3004">
                  <Button variant="flat" color="default" onClick={() => setIsPracticeModalOpen(false)} data-id="element-3005">
                    Annulla
                  </Button>
                  <Button onClick={handleSavePractice} startContent={<Save className="h-4 w-4" data-id="element-3007" />} data-id="element-3006">
                    Crea pratica
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}